/**
 * 個資編輯頁（tianyun-profile LIFF）。
 *
 * 讓使用者補改：生日、時辰、時區、性別、email。
 * 送 PATCH /me，回 200 就更新本地 state 並顯示成功提示。
 */
import { useEffect, useState, type FormEvent } from 'react';

import { getMe, patchMe, type MeProfile, ApiError } from '../lib/api';
import { useLiff } from '../lib/liff';

interface ProfilePageProps {
  liffId: string | undefined;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

// 常用時區選項（LINE 使用者大多在這幾個）。label 是使用者看到的中文說明，
// value 是送到後端的 IANA timezone。排盤用的是「出生地的當地時區」。
const TIMEZONE_OPTIONS: { label: string; value: string }[] = [
  { label: '台灣（UTC+8）', value: 'Asia/Taipei' },
  { label: '香港（UTC+8）', value: 'Asia/Hong_Kong' },
  { label: '中國大陸（UTC+8）', value: 'Asia/Shanghai' },
  { label: '日本 / 韓國（UTC+9）', value: 'Asia/Tokyo' },
  { label: '新加坡 / 馬來西亞（UTC+8）', value: 'Asia/Singapore' },
  { label: '美西（UTC-8/-7）', value: 'America/Los_Angeles' },
  { label: '美東（UTC-5/-4）', value: 'America/New_York' },
  { label: '英國（UTC+0/+1）', value: 'Europe/London' },
];

export function ProfilePage({ liffId }: ProfilePageProps) {
  const liff = useLiff(liffId);
  const [form, setForm] = useState<MeProfile>({
    birthDate: null,
    birthTime: null,
    timezone: null,
    gender: null,
    email: null,
  });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (liff.kind !== 'ready') return;
    setLoading(true);
    getMe(liff.idToken)
      .then((r) => {
        setForm(r.profile);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setLoadError('尚未完成 onboarding，請先到 LINE bot 加好友。');
        } else {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => setLoading(false));
  }, [liff]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (liff.kind !== 'ready') return;

    setSubmitState({ kind: 'saving' });

    // 只送有值的欄位；null/空字串不送（避免被 zod refine 拒為空 body）
    const payload: Record<string, string> = {};
    if (form.birthDate) payload.birthDate = form.birthDate;
    if (form.birthTime) payload.birthTime = form.birthTime;
    if (form.timezone) payload.timezone = form.timezone;
    if (form.gender) payload.gender = form.gender;
    if (form.email) payload.email = form.email;

    if (Object.keys(payload).length === 0) {
      setSubmitState({ kind: 'error', message: '請至少填入一個欄位' });
      return;
    }

    try {
      const updated = await patchMe(
        liff.idToken,
        payload as {
          birthDate?: string;
          birthTime?: string;
          timezone?: string;
          gender?: 'male' | 'female';
          email?: string;
        },
      );
      setForm(updated.profile);
      setSubmitState({ kind: 'saved' });
    } catch (err) {
      setSubmitState({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (liff.kind === 'loading') {
    return <Loading label="連接 LINE⋯" />;
  }
  if (liff.kind === 'not-logged-in') {
    return (
      <main className="flex min-h-screen items-center justify-center p-6 text-center">
        <div>
          <p className="text-sm text-ink/70">請先登入 LINE</p>
          <button
            onClick={liff.login}
            className="mt-4 rounded-full bg-accent px-6 py-2 text-sm font-medium text-cream"
          >
            登入
          </button>
        </div>
      </main>
    );
  }
  if (liff.kind === 'error') {
    return <ErrorPane message={liff.message} />;
  }

  return (
    <main className="mx-auto min-h-screen max-w-md p-4 pb-10">
      <header className="mb-4">
        <h1 className="font-serif text-2xl font-bold">個人資料</h1>
        <p className="mt-1 text-xs text-ink/60">
          生辰影響命盤計算，請填寫準確。按錯或想改隨時回來這頁更新即可。
          Email 用於年訂的「命盤月報」寄送。
        </p>
      </header>

      {loading && <Loading label="讀取資料⋯" />}

      {loadError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {loadError}
        </div>
      )}

      <form className="space-y-4" onSubmit={onSubmit}>
        <Field label="生日" hint="YYYY-MM-DD">
          <input
            type="date"
            className="w-full rounded-md border border-paper bg-cream p-2 text-sm"
            value={form.birthDate ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, birthDate: e.target.value || null }))
            }
          />
        </Field>

        <Field label="出生時間" hint="HH:mm（24 小時制）">
          <input
            type="time"
            className="w-full rounded-md border border-paper bg-cream p-2 text-sm"
            value={form.birthTime ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, birthTime: e.target.value || null }))
            }
          />
        </Field>

        <Field label="出生地時區" hint="影響命盤與每日推播時間">
          <select
            className="w-full rounded-md border border-paper bg-cream p-2 text-sm"
            value={form.timezone ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, timezone: e.target.value || null }))
            }
          >
            <option value="">選擇時區⋯</option>
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] leading-relaxed text-ink/60">
            選你「出生當時所在地」的時區，不是你現在住的地方。
            例：在台灣出生 → 台灣；在美國出生後搬回台灣 → 美西 / 美東。
          </p>
        </Field>

        <Field label="性別" hint="紫微斗數排盤需要">
          {/* 用 button + role=radio 取代 <label><input class="sr-only">：
              LINE Android WebView 上 label 點擊不一定會把 click 轉送到
              clip 掉的隱藏 radio，導致 onChange 不觸發。button 的 onClick
              在所有平台都穩定。 */}
          <div role="radiogroup" aria-label="性別" className="flex gap-3">
            {(['male', 'female'] as const).map((g) => (
              <button
                key={g}
                type="button"
                role="radio"
                aria-checked={form.gender === g}
                onClick={() => setForm((f) => ({ ...f, gender: g }))}
                className={`flex-1 rounded-md border p-2 text-center text-sm ${
                  form.gender === g
                    ? 'border-accent bg-accent/10 font-medium text-accent'
                    : 'border-paper bg-cream'
                }`}
              >
                {g === 'male' ? '男' : '女'}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Email" hint="年訂月報寄送；不會公開">
          <input
            type="email"
            className="w-full rounded-md border border-paper bg-cream p-2 text-sm"
            value={form.email ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, email: e.target.value || null }))
            }
            placeholder="you@example.com"
          />
        </Field>

        <button
          type="submit"
          disabled={submitState.kind === 'saving'}
          className="w-full rounded-full bg-accent px-6 py-3 text-sm font-medium text-cream disabled:opacity-60"
        >
          {submitState.kind === 'saving' ? '儲存中⋯' : '儲存'}
        </button>

        {submitState.kind === 'saved' && (
          <p className="text-center text-xs text-green-700">已儲存</p>
        )}
        {submitState.kind === 'error' && (
          <p className="text-center text-xs text-red-700">
            {submitState.message}
          </p>
        )}
      </form>
    </main>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-medium text-ink">{label}</span>
        {hint && <span className="text-[10px] text-ink/50">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-sm text-ink/60">
      {label}
    </div>
  );
}

function ErrorPane({ message }: { message: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center p-6 text-center">
      <div>
        <h1 className="font-serif text-lg font-bold">無法載入</h1>
        <p className="mt-2 text-xs text-ink/70 break-words">{message}</p>
      </div>
    </main>
  );
}
